<script lang="ts" setup>
import t from "~/utils/translate";
import { mdiHeart, mdiThumbUp, mdiCloseCircle } from "@mdi/js";
import { useAuth } from "~/store/auth";
import { useCasino } from "~/store/casino";
import { useSettings } from "~/store/settings";
import { IGame } from "~/types/gaming/game";
import appLaunch from "~/utils/casino/appLaunch";
import NewGameBadge from "~/components/layout/Casino/NewGameBadge.vue";
import PromotedGameBadge from "~/components/layout/Casino/PromotedGameBadge.vue";

interface ICasinoGame {
	game: IGame;
	class?: string;
	disableNavigate?: boolean;
}

const settings = useSettings();
const auth = useAuth();
const route = useRoute();
const casino = useCasino();
const { subLobby } = route.params;
const router = useRouter();
const { $trackEvent } = useNuxtApp();

const props = defineProps<ICasinoGame>();

const isMobile = useMobile();
const progressiveAmount = ref<number>(props.game.progressiveData && Number(props.game.progressiveData?.jackpotValue));
const to = `/lobby/${props.game.vertical?.toLowerCase() || subLobby}/game/${props.game.alias}?vertical=${subLobby}`;
const activeTab = computed(() => (route.params.tab as string) || "all");
const gameDiv = ref<HTMLElement>();
const mustShow = ref<boolean>(true);
const hovering = ref(false);

const hasFreePlay = computed(() => {
	return props.game.freeplay && !auth.loggedIn && settings.hasFeature("IsFreePlayEnable");
});

const minimumBetAmount = computed(() => {
	return props.game.minBetAmount?.toString().replace("R", "");
});

const setCategory = (category: string) => {
	casino.activeCategory = category;
	casino.originUrl = route.fullPath;
};

const handleClick = async (isFreePlay = false) => {
	if (props.game.unavailable) {
		return;
	}

	if (hasFreePlay.value) {
		if (!hovering.value) {
			hovering.value = true;
			return;
		}
	}
	if (casino.isGamesModalOpen) casino.isGamesModalOpen = false;

	if (settings.isNativeApp && settings.hasNativeLobby) {
		useNuxtApp().$messageHandler({
			message: "Navigate",
			data: {
				path: to,
				vertical: props.game.vertical || subLobby,
			},
		});
		const game = await appLaunch(props.game.alias, props.game.vertical);
		useNuxtApp().$messageHandler({
			message: "CasinoWidgetGameLaunch",
			data: {
				vertical: game.vertical,
				category: Object.keys(game.categories)[0],
				gameId: game.id,
				gameName: game.name,
				gameObj: game,
			},
		});

		return;
	}

	if (isMobile.value && !settings.hasNativeLobby) {
		router.push(isFreePlay ? `${to}/?freePlay=true` : to);
	}
	if (route.params.tab?.includes("provider-")) {
		setCategory(Object.keys(props.game.categories)[0].toLowerCase());
	} else {
		setCategory(activeTab.value);
	}
};

const handleFavouriteClick = (game: IGame) => {
	if (auth.loggedIn) {
		casino.setFavourite(game);
		if (casino.favourites.length < 1 && route.path.includes("/favourites")) {
			router.push(`/lobby/${game.vertical?.toLowerCase().replace(/\s/g, "") || subLobby.toString().replace(/\s/g, "")}/all`);
			casino.activeCategory = "all";
		}
	} else {
		if (settings.isNativeApp) return window.promptLogin();
		auth.modals.login = true;
	}

	$trackEvent.likeGame(game.name, game.likesCount + 1);
};
</script>

<template>
	<div
		ref="gameDiv"
		:class="['overflow-hidden text rounded-lg text-light-50 select-none ', props.class || 'w-full']"
		@click="hasFreePlay ? (hovering = !hovering) : handleClick()"
	>
		<div
			class="relative rounded-tl rounded-tr select-none"
			@mouseover="isMobile ? '' : (hovering = true)"
			@mouseleave="isMobile ? '' : (hovering = false)"
		>
			<NewGameBadge v-if="game.isNew" />
			<PromotedGameBadge v-if="game.isPromoted" />
			<UiButton
				v-if="mustShow"
				variant="custom"
				size="custom"
				:icon="mdiHeart"
				:ariaLabel="t('favourite-game')"
				position="absolute"
				rootClass="top-0 right-0 z-30 flex items-center justify-center w-8 h-8 rounded cursor-pointer bg-transparent"
				:iconClass="['w-6 h-6 transition-all', casino.isGameFavorite(game.id) ? 'fill-identity' : 'fill-light-50']"
				@click.stop="handleFavouriteClick(game)"
			/>
			<div
				v-if="mustShow && !game.unavailable"
				:class="[
					'absolute top-0 left-0 z-10 items-center justify-center w-full h-full bg-dark-800/90 transition-all duration-300',
					hovering ? 'opacity-1' : 'opacity-0',
					hasFreePlay ? 'flex' : 'hidden  md:flex',
				]"
			>
				<template v-if="hasFreePlay">
					<div class="flex flex-col items-center justify-center w-full gap-2">
						<NuxtLink :to="to" class="w-4/6" :class="hovering ? '' : 'pointer-events-none'">
							<UiButton @click="handleClick()" class="w-full" root-class="py-1" variant="identity" size="custom" :label="t('real-play')" />
						</NuxtLink>
						<NuxtLink :to="to + '/?freePlay=true'" class="w-4/6" :class="hovering ? '' : 'pointer-events-none'">
							<UiButton @click="handleClick(true)" class="w-full" root-class="py-1" variant="secondary" size="custom" :label="t('free-play')" />
						</NuxtLink>
					</div>
				</template>
				<template v-else>
					<NuxtLink :to="to" class="w-4/6">
						<UiButton @click="handleClick" class="w-full" root-class="py-1" variant="identity" size="custom" :label="t('lobbies.play')" />
					</NuxtLink>
				</template>
			</div>
			<div
				v-if="game.unavailable"
				:class="['absolute top-0 left-0 z-30 items-center justify-center w-full h-full bg-dark-800/90 flex transition-all duration-300']"
			>
				<div class="flex flex-col items-center justify-center gap-1">
					<UiIcon :path="mdiCloseCircle" :class="'z-40 w-8 fill-light-50'" />
					<label class="text-sm font-bold leading-4 text-center">{{ t("in-maintenance") }}</label>
				</div>
			</div>
			<template>
				<NuxtLink class="select-none" :class="{ 'pointer-events-none': disableNavigate }" :to="to" style="-webkit-user-drag: none">
					<div class="w-full aspect-square" @click="handleClick()">
						<UiLazyImage
							v-if="mustShow"
							class="w-full pb-[100%] select-none"
							draggable="false"
							:src="game.tileImage"
							:img-opts="{ fit: 'cover', alt: game.name }"
						/>
					</div>
				</NuxtLink>
			</template>
			<div class="relative w-full" :class="game.providerLobby ? 'aspect-[3/4] md:aspect-[4/5]' : 'aspect-square'">
				<UiLazyImage
					v-if="mustShow"
					class="w-full h-full pb-[100%] select-none"
					:src="game.tileImage"
					:img-opts="{ class: 'object-cover', fit: 'cover', alt: game.name }"
				/>
				<div v-if="game.exclusive" class="absolute bottom-[10px] w-[15%] bg-light-50 right-0">
					<img
						alt="Betway Exclusive"
						:src="`${useRuntimeConfig().public.kenticoDomain}/${mediaLibrariesPath}/betway_exclusive.svg`"
						class="max-w-[300px]"
					/>
				</div>
			</div>
			<div
				v-if="mustShow && game.progressive && game.progressiveData && progressiveAmount"
				class="absolute p-1 text-xs font-bold leading-4 rounded bg-identity top-1 left-1"
			>
				{{ formatNumber(progressiveAmount, "currency") }}
			</div>
		</div>
		<div v-if="!game.providerLobby" class="aspect-[20/4] w-full">
			<div v-if="mustShow" class="w-full px-1 py-1 rounded-bl rounded-br whitespace-nowrap bg-dark-700">
				<div class="mb-1 overflow-hidden text-xs font-bold leading-4 text-ellipsis">{{ game.name }}</div>
				<div class="flex justify-between w-full align-center">
					<div v-if="minimumBetAmount" class="overflow-hidden text-xs leading-4 capitalize text-ellipsis text-[#ffd700]">
						{{ formatNumber(+minimumBetAmount, "currency") }}
					</div>
					<div
						:class="['flex align-center overflow-hidden text-xs leading-4 capitalize text-ellipsis', game.minBetAmount ? '' : 'w-full justify-end']"
					>
						<UiIcon :path="mdiThumbUp" class="w-4 h-4 mr-1 fill-identity" />
						<span>{{ formatNumber(game.likesCount) }}</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
